import HmacSHA256 from 'crypto-js/hmac-sha256';
import encHex from 'crypto-js/enc-hex';
import 'whatwg-fetch';
import type { CustomRequest, RequestConfig } from './model';
import { ApiError } from './ApiError';

function uuid() {
  function S4() {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  }
  return S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4();
}

/**
 * sign headers with raw body
 */
function sign(url: string, _headers: Record<string, string> = {}, rawBody = '') {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const secret = process.env.RAZZLE_API_SECRET!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const apiDomain = process.env.RAZZLE_API_MOBILE!;

  const headers: Record<string, string> = { ..._headers, 'request-id': uuid() };

  if (secret && url.startsWith(apiDomain)) {
    const sessionToken = headers['session-token'] || '';
    const timestamp = Math.ceil(Date.now() / 1000);
    const path = url.replace(apiDomain, '');

    const data = sessionToken + path + timestamp + rawBody;
    const signature = HmacSHA256(data, secret).toString(encHex);
    headers['x-timestamp'] = timestamp.toString();
    headers['x-signature'] = signature;
  }

  return headers;
}


export const vanillaRequest = async <I extends Record<string, any> | FormData, O = any>(
  url: string,
  _options: RequestConfig<I>
): Promise<O> => {
  let bodyString: string | undefined;
  if (_options.body instanceof FormData) {
    bodyString = [..._options.body.entries()]
      .map(([k, v]) => `${k}=${encodeURIComponent(v as 'string' | 'boolean' | 'number')}`)
      .join('&');
  } else {
    bodyString = _options.body && JSON.stringify(_options.body);
  }

  const { interceptors: [requestInterceptor, responseInterceptor] = [], ...otherArgs } = _options;

  const request = {
    ...otherArgs,
    headers: sign(url, _options.headers, bodyString),
  } as CustomRequest<I>;

  if (_options.body instanceof FormData) {
    request.headers['Content-Type'] = 'application/x-www-form-urlencoded';
  }

  if (requestInterceptor) {
    throw new Error('[request] request interceptor has not been implemented yet!');
  }

  return fetch(url, { ...request, body: bodyString }).then(async (_response: Response) => {
    let responseJson;
    try {
      responseJson = await _response.json();
    } catch (e) {
      responseJson = {};
    }
    const response = {
      url: _response.url,
      headers: _response.headers,
      body: responseJson,
      ok: _response.ok,
      status: _response.status,
      statusText: _response.statusText,
    };

    console.log(
      `[request ${response.status}] ${response.url.split('?')[0]} ` +
        `${request.headers['request-id']} | ${response.headers.get('x-thimble-trace-id')}`
    );

    // throw an error or return new response or return undefined
    const res = await responseInterceptor?.(request, response);

    if (response.ok /** 200~299 */) {
      return res ?? response.body;
    } else {
      throw new ApiError(responseJson.error_message || response.statusText, request, response);
    }
  });
};
